import { getBackendUrl } from '@/utils/apiUrl';
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { Users, Plus, Search, Mail, Phone, MapPin, FileText, Trash2, X, Car } from 'lucide-react';

const Customers = ({ user }) => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [newCustomer, setNewCustomer] = useState({
    name: '',
    email: '',
    phone: '',
    address: ''
  });
  
  // Filter state
  const [sortBy, setSortBy] = useState('name'); // 'name', 'email', 'recent'
  const [filterByVehicle, setFilterByVehicle] = useState('all'); // 'all', 'with-vehicle', 'without-vehicle'

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async (search = '') => {
    try {
      setLoading(true);
      const url = search 
        ? `${getBackendUrl()}/api/customers?search=${encodeURIComponent(search)}`
        : `${getBackendUrl()}/api/customers`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCustomers(data);
      }
    } catch (error) {
      console.error('Fehler beim Laden der Kunden:', error);
      toast.error('Fehler beim Laden der Kunden');
    } finally {
      setLoading(false);
    }
  };

  // Filter and sort customers
  const getFilteredAndSortedCustomers = () => {
    let filtered = [...customers];
    
    // Apply vehicle filter
    if (filterByVehicle === 'with-vehicle') {
      filtered = filtered.filter(c => c.vehicles && c.vehicles.length > 0);
    } else if (filterByVehicle === 'without-vehicle') {
      filtered = filtered.filter(c => !c.vehicles || c.vehicles.length === 0);
    }
    
    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return (a.name || '').localeCompare(b.name || '');
        case 'name-desc':
          return (b.name || '').localeCompare(a.name || '');
        case 'email':
          return (a.email || '').localeCompare(b.email || '');
        case 'recent':
          return new Date(b.created_at || 0) - new Date(a.created_at || 0);
        default:
          return 0;
      }
    });
    
    return filtered;
  };

  const displayCustomers = getFilteredAndSortedCustomers();

  const handleSearch = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    
    // Debounce search
    if (query.length >= 2 || query.length === 0) {
      fetchCustomers(query);
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedCustomers(customers.map(c => c._id));
    } else {
      setSelectedCustomers([]);
    }
  };

  const handleSelectCustomer = (customerId) => {
    if (selectedCustomers.includes(customerId)) {
      setSelectedCustomers(selectedCustomers.filter(id => id !== customerId));
    } else {
      setSelectedCustomers([...selectedCustomers, customerId]);
    }
  };

  const handleDeleteSingle = async (customerId, customerName) => {
    if (!window.confirm(`Möchten Sie den Kunden "${customerName}" wirklich löschen?`)) {
      return;
    }

    try {
      setIsDeleting(true);
      const response = await fetch(`${getBackendUrl()}/api/customers/${customerId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        toast.success('Kunde wurde gelöscht');
        fetchCustomers(searchQuery);
        setSelectedCustomers(selectedCustomers.filter(id => id !== customerId));
      } else {
        toast.error('Fehler beim Löschen des Kunden');
      }
    } catch (error) {
      console.error('Fehler beim Löschen:', error);
      toast.error('Fehler beim Löschen des Kunden');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedCustomers.length === 0) return;

    if (!window.confirm(`Möchten Sie ${selectedCustomers.length} Kunden wirklich löschen?`)) {
      return;
    }

    try {
      setIsDeleting(true);
      const response = await fetch(`${getBackendUrl()}/api/customers/bulk-delete`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(selectedCustomers)
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(`${result.deleted_count} Kunden wurden gelöscht`);
        fetchCustomers(searchQuery);
        setSelectedCustomers([]);
      } else {
        toast.error('Fehler beim Löschen der Kunden');
      }
    } catch (error) {
      console.error('Fehler beim Löschen:', error);
      toast.error('Fehler beim Löschen der Kunden');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCreateCustomer = async (e) => {
    e.preventDefault();
    
    if (!newCustomer.name.trim()) {
      toast.error('Bitte geben Sie einen Namen ein');
      return;
    }

    try {
      setIsCreating(true);
      const response = await fetch(`${getBackendUrl()}/api/customers`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newCustomer)
      });

      if (response.ok) {
        toast.success('Kunde wurde erstellt');
        setShowCreateModal(false);
        setNewCustomer({ name: '', email: '', phone: '', address: '' });
        fetchCustomers(searchQuery);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Fehler beim Erstellen des Kunden');
      }
    } catch (error) {
      console.error('Fehler beim Erstellen:', error);
      toast.error('Fehler beim Erstellen des Kunden');
    } finally {
      setIsCreating(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric' 
    });
  };

  return (
    <Layout user={user}>
      <div className="p-4 md:p-8">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6 md:mb-8">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Kunden</h1>
            <p className="text-sm md:text-base text-gray-600 mt-1">
              {customers.length} {customers.length === 1 ? 'Kunde' : 'Kunden'} • Automatisch erfasst aus Fällen
            </p>
          </div>
          <div className="flex gap-3">
            {selectedCustomers.length > 0 && (
              <button
                onClick={handleBulkDelete}
                disabled={isDeleting}
                className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2 text-white text-sm font-medium rounded-lg transition-all min-h-[44px]"
                style={{backgroundColor: '#DC2626'}}
                data-testid="bulk-delete-button"
              >
                <Trash2 className="w-4 h-4" />
                {isDeleting ? 'Wird gelöscht...' : `${selectedCustomers.length} Kunden löschen`}
              </button>
            )}
            <button
              onClick={() => setShowCreateModal(true)}
              className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2 text-white text-sm font-medium rounded-lg transition-all min-h-[44px]"
              style={{backgroundColor: '#0A4D8C'}}
              data-testid="add-customer-button"
            >
              <Plus className="w-4 h-4" />
              Kunde anlegen
            </button>
          </div>
        </div>

        {/* Search & Filters */}
        <div className="mb-4 md:mb-6 space-y-4">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search Input */}
            <div className="relative flex-1">
              <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Kunden suchen nach Name, E-Mail oder Telefon..."
                value={searchQuery}
                onChange={handleSearch}
                className="w-full pl-10 pr-4 py-2 md:py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm md:text-base"
                style={{borderColor: '#E1E4E8'}}
                data-testid="search-customers-input"
              />
            </div>
            
            {/* Sort Filter */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-4 py-2 md:py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm md:text-base"
              style={{borderColor: '#E1E4E8'}}
              data-testid="sort-customers-select"
            >
              <option value="name">Sortieren: Name (A-Z)</option>
              <option value="name-desc">Sortieren: Name (Z-A)</option>
              <option value="recent">Sortieren: Neueste zuerst</option>
              <option value="email">Sortieren: E-Mail</option>
            </select>
            
            {/* Vehicle Filter */}
            <select
              value={filterByVehicle}
              onChange={(e) => setFilterByVehicle(e.target.value)}
              className="px-4 py-2 md:py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm md:text-base"
              style={{borderColor: '#E1E4E8'}}
              data-testid="filter-vehicle-select"
            >
              <option value="all">Alle Kunden</option>
              <option value="with-vehicle">Mit Fahrzeug</option>
              <option value="without-vehicle">Ohne Fahrzeug</option>
            </select>
          </div>
        </div>

        {/* Customers Grid */}
        <div className="bg-white rounded-lg shadow-sm border" style={{borderColor: '#E1E4E8'}}>
          {loading ? (
            <div className="p-6">
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-4 border-gray-200 mx-auto" style={{borderTopColor: '#0A4D8C'}}></div>
              </div>
            </div>
          ) : displayCustomers.length === 0 ? (
            <div className="p-6">
              <div className="text-center py-12 text-gray-500">
                <Users className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <p className="font-medium">Keine Kunden vorhanden</p>
                <p className="text-sm mt-2">
                  Kunden werden automatisch erstellt, wenn Sie einen neuen Fall anlegen
                </p>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50" style={{borderBottom: '1px solid #E1E4E8'}}>
                  <tr>
                    <th className="px-6 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={selectedCustomers.length === customers.length && customers.length > 0}
                        onChange={handleSelectAll}
                        className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        data-testid="select-all-checkbox"
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Kunde
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Kontakt
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Fahrzeuge
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Fälle
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Letzter Fall
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Aktionen
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y" style={{borderColor: '#E1E4E8'}}>
                  {displayCustomers.map((customer) => (
                    <tr 
                      key={customer._id} 
                      className="hover:bg-gray-50 transition-colors"
                      data-testid={`customer-${customer._id}`}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={selectedCustomers.includes(customer._id)}
                          onChange={() => handleSelectCustomer(customer._id)}
                          className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          data-testid={`checkbox-${customer._id}`}
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          {customer.profile_picture ? (
                            <img 
                              src={customer.profile_picture} 
                              alt={customer.name}
                              className="w-10 h-10 rounded-full object-cover border-2 border-blue-100"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-medium" style={{backgroundColor: '#0A4D8C'}}>
                              {customer.name.charAt(0).toUpperCase()}
                            </div>
                          )}
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {customer.name}
                            </div>
                            <div className="text-xs text-gray-500">
                              Seit {formatDate(customer.created_at)}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="space-y-1">
                          {customer.email && (
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <Mail className="w-4 h-4 text-gray-400" />
                              <a href={`mailto:${customer.email}`} className="hover:text-blue-600">
                                {customer.email}
                              </a>
                            </div>
                          )}
                          {customer.phone && (
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <Phone className="w-4 h-4 text-gray-400" />
                              <a href={`tel:${customer.phone}`} className="hover:text-blue-600">
                                {customer.phone}
                              </a>
                            </div>
                          )}
                          {!customer.email && !customer.phone && (
                            <span className="text-sm text-gray-400">Keine Kontaktdaten</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {customer.vehicles && customer.vehicles.length > 0 ? (
                          <div className="space-y-1">
                            {customer.vehicles.slice(0, 2).map((vehicle, idx) => (
                              <div key={idx} className="flex items-center gap-2 text-sm text-gray-600">
                                <Car className="w-4 h-4 text-gray-400" />
                                <span className="font-medium">{vehicle.license_plate}</span>
                                <span className="text-gray-500">•</span>
                                <span>{vehicle.manufacturer} {vehicle.model}</span>
                              </div>
                            ))}
                            {customer.vehicles.length > 2 && (
                              <div className="text-xs text-gray-500 pl-6">
                                +{customer.vehicles.length - 2} weitere
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">Keine Fahrzeuge</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        <div className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium" style={{backgroundColor: '#DBEAFE', color: '#2563EB'}}>
                          <FileText className="w-3 h-3" />
                          {customer.total_cases || 0}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {formatDate(customer.last_case_date)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <button
                          onClick={() => handleDeleteSingle(customer._id, customer.name)}
                          disabled={isDeleting}
                          className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                          data-testid={`delete-${customer._id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                          Löschen
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Create Customer Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full" data-testid="create-customer-modal">
              <div className="flex items-center justify-between p-6 border-b" style={{borderColor: '#E1E4E8'}}>
                <h2 className="text-xl font-semibold text-gray-900">Neuen Kunden anlegen</h2>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                  data-testid="close-modal-button"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <form onSubmit={handleCreateCustomer} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={newCustomer.name}
                    onChange={(e) => setNewCustomer({...newCustomer, name: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                    style={{borderColor: '#E1E4E8'}}
                    placeholder="Max Mustermann"
                    required
                    data-testid="customer-name-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    E-Mail
                  </label>
                  <input
                    type="email"
                    value={newCustomer.email}
                    onChange={(e) => setNewCustomer({...newCustomer, email: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                    style={{borderColor: '#E1E4E8'}}
                    placeholder="max@beispiel.de"
                    data-testid="customer-email-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Telefon
                  </label>
                  <input
                    type="tel"
                    value={newCustomer.phone}
                    onChange={(e) => setNewCustomer({...newCustomer, phone: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                    style={{borderColor: '#E1E4E8'}}
                    placeholder="+49 123 456789"
                    data-testid="customer-phone-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Adresse
                  </label>
                  <textarea
                    value={newCustomer.address}
                    onChange={(e) => setNewCustomer({...newCustomer, address: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                    style={{borderColor: '#E1E4E8'}}
                    placeholder="Musterstraße 123, 12345 Musterstadt"
                    rows="2"
                    data-testid="customer-address-input"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="flex-1 px-4 py-2 border text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
                    style={{borderColor: '#E1E4E8'}}
                    data-testid="cancel-button"
                  >
                    Abbrechen
                  </button>
                  <button
                    type="submit"
                    disabled={isCreating}
                    className="flex-1 px-4 py-2 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
                    style={{backgroundColor: '#0A4D8C'}}
                    data-testid="submit-button"
                  >
                    {isCreating ? 'Wird erstellt...' : 'Kunde erstellen'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Customers;
